// A client's readiness baseline: their own normal range for the daily total and for each question,
// from their questionnaires of the last 6 weeks up to a given day. Port of msb-mobile
// src/helpers/readiness/baseline.ts; both must give the same numbers for the same data.

import {
  ANSWER_MAX,
  ANSWER_MIN,
  BASELINE_MIN_CHECK_INS,
  BASELINE_WINDOW_DAYS,
  INCLUDE_CURRENT_IN_NORMAL,
  READINESS_KEYS,
} from "./constants";
import { utcToDayNum } from "./dates";
import { computeNormalRange, toWholeNumberRange } from "./normalRange";

// True when all five answers are real answers, 1 to 5. A partial entry never counts as a
// questionnaire, and neither does a day whose answers are 0: that is a daily stat that only holds
// something else (an HRV reading, say) next to an empty readiness object.
export const isCompleteReadiness = (readiness) =>
  Boolean(readiness) &&
  READINESS_KEYS.every(
    (key) =>
      Number.isFinite(readiness[key]) &&
      readiness[key] >= ANSWER_MIN &&
      readiness[key] <= ANSWER_MAX
  );

// Sum of the five answers (an extra wearable `score` on the object is not part of it)
export const getReadinessTotal = (readiness) =>
  READINESS_KEYS.reduce((sum, key) => sum + readiness[key], 0);

// The days with a complete questionnaire, by calendar day number. When a day shows up twice the
// last one in the list wins.
export const toReadinessEntries = (stats) => {
  const byDay = new Map();
  for (const stat of stats ?? []) {
    if (!isCompleteReadiness(stat?.readiness)) continue;
    const day = utcToDayNum(stat.utcDate);
    byDay.set(day, {
      day,
      utcDate: Number(stat.utcDate),
      readiness: stat.readiness,
    });
  }
  return byDay;
};

// The date (YYYYMMDD) of the client's first complete questionnaire, or null when there is none
export const firstQuestionnaireUtc = (stats) => {
  let first = null;
  for (const entry of toReadinessEntries(stats).values()) {
    if (first === null || entry.utcDate < first) first = entry.utcDate;
  }
  return first;
};

// The entries a day is compared against: the 6-week window ending on `day`, minus the day's own
// questionnaire unless `includeCurrent` is true.
export const selectBaselineEntries = (
  entriesByDay,
  day,
  includeCurrent = INCLUDE_CURRENT_IN_NORMAL
) => {
  const entries = [];
  const first = day - (BASELINE_WINDOW_DAYS - 1);
  const last = includeCurrent ? day : day - 1;
  for (let current = first; current <= last; current++) {
    const entry = entriesByDay.get(current);
    if (entry) entries.push(entry);
  }
  return entries;
};

// `ready` is false below `minCheckIns`: then no range is returned (the client is "learning").
export const buildBaseline = (
  entries,
  minCheckIns = BASELINE_MIN_CHECK_INS
) => {
  const checkIns = entries.length;
  const baseline = {
    ready: checkIns >= minCheckIns,
    checkIns,
    total: null,
    sore: null,
    stress: null,
    energy: null,
    mood: null,
    sleep: null,
  };
  if (!baseline.ready) return baseline;

  const rangeOf = (values) => toWholeNumberRange(computeNormalRange(values));
  baseline.total = rangeOf(
    entries.map((entry) => getReadinessTotal(entry.readiness))
  );
  for (const key of READINESS_KEYS) {
    baseline[key] = rangeOf(entries.map((entry) => entry.readiness[key]));
  }
  return baseline;
};

export const buildBaselineAt = (
  entriesByDay,
  day,
  includeCurrent = INCLUDE_CURRENT_IN_NORMAL
) => buildBaseline(selectBaselineEntries(entriesByDay, day, includeCurrent));
